import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Loader2
} from 'lucide-react';

const prioridadColores = {
  URGENTE: 'bg-red-500',
  ALTA: 'bg-orange-500',
  MEDIA: 'bg-yellow-500',
  BAJA: 'bg-green-500'
};

const TramitesCalendario = ({ onTramiteClick, filtros = {} }) => {
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [año, setAño] = useState(new Date().getFullYear());
  const [tramitesPorDia, setTramitesPorDia] = useState({});
  const [loading, setLoading] = useState(true);

  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const diasSemana = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const loadCalendario = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('mes', mes);
      params.append('año', año);
      if (filtros.responsable_id) params.append('responsable_id', filtros.responsable_id);
      
      const res = await api.get(`/tramites/calendario?${params.toString()}`);
      setTramitesPorDia(res.data.tramites_por_dia || {});
    } catch (error) {
      console.error('Error loading calendario:', error);
      toast.error('Error al cargar calendario');
    } finally {
      setLoading(false);
    }
  }, [mes, año, filtros]);

  useEffect(() => {
    loadCalendario();
  }, [loadCalendario]);

  const cambiarMes = (delta) => {
    let nuevoMes = mes + delta;
    let nuevoAño = año;
    
    if (nuevoMes > 12) {
      nuevoMes = 1;
      nuevoAño++;
    } else if (nuevoMes < 1) {
      nuevoMes = 12;
      nuevoAño--;
    }
    
    setMes(nuevoMes);
    setAño(nuevoAño);
  };

  const getDiasDelMes = () => {
    const primerDia = new Date(año, mes - 1, 1);
    const ultimoDia = new Date(año, mes, 0);
    const diasEnMes = ultimoDia.getDate();
    const diaSemanaInicio = primerDia.getDay();
    
    const dias = [];
    
    // Días vacíos al inicio
    for (let i = 0; i < diaSemanaInicio; i++) {
      dias.push(null);
    }
    
    // Días del mes
    for (let i = 1; i <= diasEnMes; i++) {
      dias.push(i);
    }
    
    return dias;
  };

  const formatFecha = (dia) => {
    return `${año}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
  };

  const esHoy = (dia) => {
    const hoy = new Date();
    return hoy.getDate() === dia && hoy.getMonth() + 1 === mes && hoy.getFullYear() === año;
  };

  const dias = getDiasDelMes();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  return (
    <Card className="p-4">
      {/* Header del calendario */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="outline" size="sm" onClick={() => cambiarMes(-1)}>
          <ChevronLeft size={18} />
        </Button>
        <h3 className="text-lg font-semibold text-slate-900">
          {meses[mes - 1]} {año}
        </h3>
        <Button variant="outline" size="sm" onClick={() => cambiarMes(1)}>
          <ChevronRight size={18} />
        </Button>
      </div>

      {/* Días de la semana */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {diasSemana.map(dia => (
          <div key={dia} className="text-center text-sm font-medium text-slate-500 py-2">
            {dia}
          </div>
        ))}
      </div>

      {/* Grilla del calendario */}
      <div className="grid grid-cols-7 gap-1">
        {dias.map((dia, index) => {
          if (dia === null) {
            return <div key={`empty-${index}`} className="h-24 bg-slate-50 rounded" />;
          }
          
          const fecha = formatFecha(dia);
          const tramitesDelDia = tramitesPorDia[fecha] || [];
          const hayVencidos = tramitesDelDia.some(t => t.estado !== 'COMPLETADO');
          
          return (
            <div
              key={dia}
              className={`h-24 border rounded p-1 overflow-hidden ${
                esHoy(dia) ? 'bg-indigo-50 border-indigo-300' : 'bg-white border-slate-200'
              }`}
            >
              <div className={`text-sm font-medium mb-1 ${
                esHoy(dia) ? 'text-indigo-600' : 'text-slate-700'
              }`}>
                {dia}
              </div>
              
              <div className="space-y-1 overflow-y-auto max-h-[60px]">
                {tramitesDelDia.slice(0, 3).map((tramite) => (
                  <div
                    key={tramite.id}
                    onClick={() => onTramiteClick?.(tramite.id)}
                    className={`text-xs p-1 rounded cursor-pointer hover:opacity-80 truncate ${
                      tramite.estado === 'COMPLETADO' ? 'bg-green-100 text-green-800' :
                      'bg-slate-100 text-slate-700'
                    }`}
                    title={tramite.titulo}
                  >
                    <span className={`inline-block w-2 h-2 rounded-full mr-1 ${prioridadColores[tramite.prioridad]}`} />
                    {tramite.titulo}
                  </div>
                ))}
                {tramitesDelDia.length > 3 && (
                  <div className="text-xs text-slate-400 text-center">
                    +{tramitesDelDia.length - 3} más
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Leyenda */}
      <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-red-500" /> Urgente
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-orange-500" /> Alta
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-yellow-500" /> Media
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-green-500" /> Baja
        </div>
      </div>
    </Card>
  );
};

export default TramitesCalendario;
